import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Truck, Wrench, RotateCcw, ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO_IMG = "https://images.pexels.com/photos/15677753/pexels-photo-15677753.jpeg?auto=compress&cs=tinysrgb&w=1400";
const CAT_IMGS = {
  Catene: "https://images.pexels.com/photos/4206113/pexels-photo-4206113.jpeg?auto=compress&cs=tinysrgb&w=1200",
  Barre: "https://images.unsplash.com/photo-1550305490-75e5ba3e301c?auto=format&fit=crop&w=1000&q=80",
  Ricambi: "https://images.unsplash.com/photo-1515433868209-994b50c7e2f1?auto=format&fit=crop&w=1000&q=80",
  Oli: "https://images.pexels.com/photos/15677753/pexels-photo-15677753.jpeg?auto=compress&cs=tinysrgb&w=1000",
};
const BRANDS = ["PRO.TOP"];

function RevealLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.76, 0, 0.24, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function CategoryCell({ title, sub, img, to, className = "", testId }) {
  return (
    <Link data-testid={testId} to={to} className={`relative overflow-hidden group border border-[#e5e5e5] ${className}`}>
      <img src={img} alt={title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-black/35 group-hover:bg-black/45 transition-colors duration-300" />
      <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
        <div className="text-white/70 text-xs font-bold uppercase tracking-[0.2em]">{sub}</div>
        <div className="mt-1 flex items-center gap-2 text-white font-display font-semibold text-2xl sm:text-3xl uppercase tracking-tight">
          {title}
          <ArrowUpRight className="w-6 h-6 text-[#ffd600] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} />
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, -70]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data.items)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center min-h-[82vh] py-14">
          <div>
            <RevealLine delay={0.1}>
              <span className="overline-tag">Ricambi & catene per motoseghe</span>
            </RevealLine>
            <h1 className="mt-4 font-display font-semibold uppercase tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              <RevealLine delay={0.2}>Taglio</RevealLine>
              <RevealLine delay={0.32}>
                <span className="text-[#ff4500]">perfetto.</span>
              </RevealLine>
              <RevealLine delay={0.44}>Potenza pura.</RevealLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-6 text-muted-foreground max-w-md text-base leading-relaxed"
            >
              Catene al giusto prezzo, trova il pezzo giusto in pochi secondi e ricevilo in 24 ore.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link data-testid="hero-cta-catalogo" to="/catalogo" className="btn-hard">
                Esplora il catalogo <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
              <Link data-testid="hero-cta-catene" to="/catalogo?categoria=Catene" className="btn-outline-hard">
                Catene più vendute
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-12 grid grid-cols-2 gap-4 max-w-md"
            >
              {[
                ["500+", "Ricambi a catalogo"],
                ["24h", "Spedizione"],
              ].map(([n, l]) => (
                <div key={l} className="border-l-2 border-[#ff4500] pl-3">
                  <div className="font-display font-semibold text-2xl">{n}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mt-0.5">{l}</div>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <motion.div style={{ y: yImg }} className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#0a0a0a]" />
              <img src={HERO_IMG} alt="Boscaiolo che taglia legna nel bosco" className="relative w-full h-[520px] object-cover" />
              <div className="absolute bottom-6 -left-10 bg-[#062e20] text-white p-5 shadow-[6px_6px_0px_#ff4500]">
                <div className="text-xs uppercase tracking-[0.2em] text-white/60">Pronta consegna</div>
                <div className="font-display font-semibold text-lg mt-1">Catene e barre PRO.TOP</div>
                <div className="font-mono text-[#ffd600] text-sm mt-0.5">Tutte le misure · 3/8 LP · .325 · 1/4</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section data-testid="brand-marquee" className="border-y border-[#e5e5e5] bg-white py-6 overflow-hidden">
        <Marquee speed={35} gradient={false}>
          {[...BRANDS, ...BRANDS, ...BRANDS, ...BRANDS].map((b, i) => (
            <span key={i} className="mx-8 font-display font-medium text-3xl sm:text-4xl uppercase tracking-tight text-[#8c8c8c] flex items-center gap-8">
              {b} <span className="text-[#ff4500] text-xl">•</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="overline-tag">Trova subito il pezzo giusto</div>
          <h2 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl md:text-5xl">
            Categorie in primo piano
          </h2>
        </motion.div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
          <CategoryCell
            testId="bento-catene"
            title="Catene su misura"
            sub="Tutte le misure · 3/8 LP · 3/8 · .325 · .325 Mini · 1/4 · 1/4 Mini"
            img={CAT_IMGS.Catene}
            to="/catalogo?categoria=Catene"
            className="md:col-span-8 h-72 md:h-96"
          />
          <CategoryCell
            testId="bento-barre"
            title="Barre guida"
            sub="Da 35 a 50 cm"
            img={CAT_IMGS.Barre}
            to="/catalogo?categoria=Barre"
            className="md:col-span-4 h-72 md:h-96"
          />
          <CategoryCell
            testId="bento-ricambi"
            title="Ricambi"
            sub="Candele · Filtri · Pompe olio"
            img={CAT_IMGS.Ricambi}
            to="/catalogo?categoria=Ricambi"
            className="md:col-span-4 h-64"
          />
          <CategoryCell
            testId="bento-pompe-olio"
            title="Pompe olio"
            sub="Ricambi per lubrificazione catena"
            img={CAT_IMGS.Oli}
            to="/catalogo?categoria=Pompe%20Olio"
            className="md:col-span-4 h-64"
          />
          <Link
            data-testid="bento-catalogo"
            to="/catalogo"
            className="md:col-span-4 h-64 bg-[#ff4500] text-white p-8 flex flex-col justify-between group hover:bg-[#e03e00] transition-colors"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Vedi tutto</div>
            <div>
              <div className="font-display font-semibold text-3xl uppercase tracking-tight leading-none">Catalogo completo</div>
              <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                Sfoglia ora <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between">
          <div>
            <div className="overline-tag">Scelti dai professionisti</div>
            <h2 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl md:text-5xl">Più venduti</h2>
          </div>
          <Link data-testid="featured-view-all" to="/catalogo" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide hover:text-[#ff4500] transition-colors">
            Vedi tutti <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
        <div data-testid="featured-products-grid" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.id} p={p} index={i} />
          ))}
        </div>
      </section>

      {/* MANIFESTO / BENEFITS */}
      <section className="mt-20 bg-[#062e20] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { n: "01", icon: Truck, title: "Spedizione rapida 24h", text: "Ordina entro le 15:00 e il tuo ricambio parte oggi stesso. Consegna in 24/48 ore; le consegne si allungano per Sud e Isole per la distanza geografica." },
            { n: "02", icon: Wrench, title: "Esperti del settore", text: "Non sai che passo ha la tua catena? Scrivici: ti aiutiamo a trovare il ricambio esatto per la tua motosega." },
            { n: "03", icon: RotateCcw, title: "Reso semplice", text: "Hai sbagliato misura? Hai 30 giorni per il reso gratuito, senza domande." },
          ].map(({ n, icon: Icon, title, text }, i) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="border-t-2 border-[#ff4500] pt-6"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[#ffd600] text-sm">{n}</span>
                <Icon className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
              </div>
              <h3 className="mt-4 font-display font-medium text-2xl uppercase tracking-tight">{title}</h3>
              <p className="mt-3 text-white/60 text-sm leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
